import { Link, createFileRoute } from '@tanstack/react-router';
import {
  BoxIcon,
  PaletteIcon,
  ShapesIcon,
  SparklesIcon,
  TypeIcon,
} from 'lucide-react';

import { Text } from '@/components/ui/text';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Section } from '@/features/design-system/showcase-primitives';

export const Route = createFileRoute('/design-system/')({
  component: DesignSystemOverview,
});

const SECTIONS = [
  {
    to: '/design-system/tokens',
    title: 'Tokens',
    description:
      'Surfaces, domain category colors, room palette, canvas accents, semantic tones, radii, spacing, and the type scale.',
    icon: PaletteIcon,
  },
  {
    to: '/design-system/components',
    title: 'Components',
    description:
      'Live primitives — buttons, badges, cards, dialog, inputs, select, switch, tooltip, toaster — every variant and size rendered from src/components/ui.',
    icon: ShapesIcon,
  },
  {
    to: '/design-system/icons',
    title: 'Icons & glyphs',
    description:
      'Lucide icon grid (chrome icons) plus the small set of Unicode glyphs the codebase uses inline (✕ ✎ ⬠ ⬟ ←).',
    icon: BoxIcon,
  },
  {
    to: '/design-system/brand',
    title: 'Brand',
    description:
      'Logo, mark, favicon, voice & tone, canonical vocabulary, and writing examples lifted from the codebase.',
    icon: SparklesIcon,
  },
] as const;

function DesignSystemOverview() {
  return (
    <div className="flex flex-col gap-12">
      <header className="flex flex-col gap-3">
        <Text variant="eyebrow">Internal · v1.4.0</Text>
        <Text variant="display" as="h1">
          The Lights Planner system
        </Text>
        <Text variant="body" tone="muted" className="max-w-2xl">
          One source of truth for tokens, components, and writing voice.
          Anything that ships uses these primitives — if a screen needs
          something not in here, fix it here first.
        </Text>
      </header>

      <Section
        eyebrow="What's inside"
        title="Sections"
        description="Each section renders the real source — no static HTML, no copies of CSS variables. Edit a primitive and the showcase updates."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.to}
                to={s.to}
                className="group rounded-xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <Card className="h-full transition-colors group-hover:bg-muted/40">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Icon className="size-4 text-muted-foreground" />
                      {s.title}
                    </CardTitle>
                    <CardDescription>{s.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </Section>

      <Section
        eyebrow="Rules of the road"
        title="When working here"
        description="Three principles to keep the system honest."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card size="sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TypeIcon className="size-3.5 text-muted-foreground" />
                One Text primitive
              </CardTitle>
              <CardDescription>
                Use <code className="font-mono text-xs">Text variant=…</code>{' '}
                for everything. Eight semantic variants cover every label in
                the app.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PaletteIcon className="size-3.5 text-muted-foreground" />
                No free-form colors
              </CardTitle>
              <CardDescription>
                Surfaces from the neutral ramp, accents from the domain
                tokens, rooms from the 16-color palette. No HSL pickers.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SparklesIcon className="size-3.5 text-muted-foreground" />
                Quiet by default
              </CardTitle>
              <CardDescription>
                1px borders, ring-based elevation, no drop shadows in chrome,
                terse second-person copy. Loudness is reserved for awareness
                events.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Section>
    </div>
  );
}
